import { AnimatePresence } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { useImagePicker } from '@/features/common/components/ImagePicker/useImagePicker';
import {
  useEffectiveExperience,
  usePageExperienceContext,
} from '@/features/experiences/contexts/PageExperienceContext';
import { useRemoveImageForExperience } from '@/features/experiences/hooks/useRemoveImageForExperience';
import { useUploadImageForExperience } from '@/features/experiences/hooks/useUploadImageForExperience';
import { ExperienceImageTypes } from '@/types/gql.generated';
import { Flex, Image, MotionBox, useToast } from '@/ui';
import { Container } from './Container';
import { Controls } from './Controls';
import { getImageUrl, getThemeUrl } from './image.lib';
import { ImageRead, type Props as ReadProps } from './Image.read';

export type Props = ReadProps & {
  recommended?: ReactNode;
};

export const ImageWrite = ({
  imageType,
  recommended,
  ...containerProps
}: Props) => {
  const { experience } = usePageExperienceContext();
  const effectiveExperience = useEffectiveExperience();
  const toast = useToast();
  const { open: openImagePicker } = useImagePicker();
  const imageUrl = experience ? getImageUrl(experience, imageType) : null;
  const [preview, setPreview] = useState<string | null>(imageUrl);

  // A logo belongs to the owning planner, so an upload from any view reaches all
  // of them; a banner and a hero stay the view's own. Everything keyed to who
  // owns the image resolves against this: the mutations, and the theme check
  // that decides whether the slot is editable at all. Plain reads need no such
  // split — ChildExperienceLoader grafts the planner's logo url onto the child.
  const owner =
    imageType === ExperienceImageTypes.Logo ? effectiveExperience : experience;

  const { uploadAsset, isPending: isUploadingImage } =
    useUploadImageForExperience(owner?.id);

  const { removeAsset, isPending: isRemovingImage } =
    useRemoveImageForExperience(owner);

  useEffect(() => {
    setPreview(imageUrl);
  }, [imageUrl]);

  const handleReplace = (file: File) => {
    const prevPreview = preview;

    uploadAsset(imageType, file, {
      onError: () => {
        toast.error('Error saving image');
        setPreview(prevPreview);
      },
    });
  };

  const handleRemove = () => {
    const prevPreview = preview;
    setPreview(null);

    removeAsset(imageType, {
      onError: () => {
        toast.error('Error removing image');
        setPreview(prevPreview);
      },
    });
  };

  return (
    <AnimatePresence mode="popLayout">
      <MotionBox
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        key={String(owner && getThemeUrl(owner, imageType))}
        transition={{ duration: 0.3 }}
      >
        {owner && getThemeUrl(owner, imageType) ? (
          <ImageRead imageType={imageType} {...containerProps} />
        ) : (
          <Container
            _hover={{ shadow: '0px 0px 4px 1px rgba(0, 0, 0, 0.1)' }}
            align="stretch"
            borderRadius="var(--experience-card-radius)"
            justify="stretch"
            noValidate
            pos="relative"
            {...containerProps}
          >
            {preview && (
              <Flex align="center" justify="center" w="100%">
                <Image
                  borderRadius="var(--experience-card-radius)"
                  src={preview}
                />
              </Flex>
            )}
            <Controls
              hasImage={Boolean(preview)}
              isRemovingImage={isRemovingImage}
              isUploadingImage={isUploadingImage}
              recommended={recommended}
              onRemoveClick={handleRemove}
              onUploadClick={() =>
                openImagePicker({
                  onSelect: (file) => {
                    const fileReader = new FileReader();
                    fileReader.onloadend = () =>
                      setPreview(fileReader.result as string);
                    if (file) {
                      fileReader.readAsDataURL(file);
                    }
                    handleReplace(file);
                  },
                })
              }
            />
          </Container>
        )}
      </MotionBox>
    </AnimatePresence>
  );
};
